/**
 * useEffectiveData — Merges live data with test/mock data when testData toggle is active.
 *
 * Produces "effective" versions of orders, positions, titles, dates, outcomes,
 * consolidatedGames, and sportsGames that downstream components consume.
 */

import { useMemo } from 'react';
import { useSamplePositions } from '@/hooks/useSamplePositions';
import type { generateMockTraderState } from '@/mock/trader';
import type { Position } from '@/types';
import type { KalshiOrder } from '@/lib/kalshiApi';
import type { ConsolidatedGameBooks } from '@/lib/nbaConsolidated/types';
import type { GameData } from '@/lib/sportsStream';

interface UseEffectiveDataParams {
  testData: boolean;
  mockTraderState?: ReturnType<typeof generateMockTraderState>;
  orders?: KalshiOrder[];
  positions?: Position[];
  positionTitleByTicker?: Record<string, string>;
  positionGameDateByTicker?: Record<string, string>;
  positionOutcomeByTicker?: Record<string, { yes?: string; no?: string }>;
  consolidatedGames: ConsolidatedGameBooks[];
  sportsGames: Map<string, GameData[]>;
}

export interface EffectiveDataResult {
  effectiveOrders: KalshiOrder[];
  effectivePositions: Position[];
  effectiveTitles?: Record<string, string>;
  effectiveDates?: Record<string, string>;
  effectiveOutcomes?: Record<string, { yes?: string; no?: string }>;
  effectiveConsolidated: ConsolidatedGameBooks[];
  effectiveSportsGames: Map<string, GameData[]>;
}

export function useEffectiveData({
  testData,
  mockTraderState,
  orders,
  positions,
  positionTitleByTicker,
  positionGameDateByTicker,
  positionOutcomeByTicker,
  consolidatedGames,
  sportsGames,
}: UseEffectiveDataParams): EffectiveDataResult {
  const sample = useSamplePositions(mockTraderState);

  const effectiveOrders = useMemo(
    () => (testData ? [...(orders ?? []), ...sample.orders] : (orders ?? [])),
    [testData, orders, sample.orders]
  );

  const effectivePositions = useMemo(
    () => (testData ? [...(positions ?? []), ...sample.positions] : (positions ?? [])),
    [testData, positions, sample.positions]
  );

  const effectiveTitles = useMemo(
    () =>
      testData ? { ...positionTitleByTicker, ...sample.titleByTicker } : positionTitleByTicker,
    [testData, positionTitleByTicker, sample.titleByTicker]
  );

  const effectiveDates = useMemo(
    () =>
      testData ? { ...positionGameDateByTicker, ...sample.dateByTicker } : positionGameDateByTicker,
    [testData, positionGameDateByTicker, sample.dateByTicker]
  );

  const effectiveOutcomes = useMemo(
    () =>
      testData
        ? { ...positionOutcomeByTicker, ...sample.outcomeByTicker }
        : positionOutcomeByTicker,
    [testData, positionOutcomeByTicker, sample.outcomeByTicker]
  );

  const effectiveConsolidated = useMemo(
    () => (testData ? [...consolidatedGames, ...sample.consolidatedGames] : consolidatedGames),
    [testData, consolidatedGames, sample.consolidatedGames]
  );

  const effectiveSportsGames = useMemo(() => {
    if (!testData) return sportsGames;
    const merged = new Map(sportsGames);
    for (const [key, games] of sample.sportsGames) {
      merged.set(key, [...(merged.get(key) ?? []), ...games]);
    }
    return merged;
  }, [testData, sportsGames, sample.sportsGames]);

  return {
    effectiveOrders,
    effectivePositions,
    effectiveTitles,
    effectiveDates,
    effectiveOutcomes,
    effectiveConsolidated,
    effectiveSportsGames,
  };
}
